import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, Mic, TrendingUp, TrendingDown, Minus,
  Target, Award, BarChart3, Calendar
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PronunciationHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${API}/pronunciation/history?limit=100`);
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load pronunciation data');
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const stats = data?.stats || {};
  const trend = data?.trend || [];
  const history = data?.history || [];

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-500';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-500/10 border-emerald-500/30';
    if (score >= 60) return 'bg-amber-500/10 border-amber-500/30';
    return 'bg-red-500/10 border-red-500/30';
  };

  const improvementIcon = stats.improvement > 0
    ? <TrendingUp className="h-5 w-5 text-emerald-500" />
    : stats.improvement < 0
    ? <TrendingDown className="h-5 w-5 text-red-400" />
    : <Minus className="h-5 w-5 text-slate-400" />;

  return (
    <div className="min-h-screen bg-background" data-testid="pronunciation-history-page">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft className="h-5 w-5" />
            <span className="text-sm">Back</span>
          </Link>
          <h1 className="font-bold text-primary flex items-center gap-2">
            <Mic className="h-5 w-5" /> Pronunciation History
          </h1>
          <div className="w-20" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={`skeleton-${i}`} className="h-28 bg-muted animate-pulse rounded-xl" />
            ))}
          </div>
        ) : stats.total_attempts === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Mic className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
              <h2 className="text-xl font-bold mb-2">No Pronunciation Data Yet</h2>
              <p className="text-muted-foreground mb-6">
                Start practicing pronunciation in lessons or Pflege Training to see your progress here.
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/pflege-training">
                  <Button>Go to Pflege Training</Button>
                </Link>
                <Link to="/lessons">
                  <Button variant="outline">Go to Lessons</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pronunciation-stats">
              <Card className="border-t-4 border-t-teal-500">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Avg Score</p>
                      <p className={`text-3xl font-bold mt-1 ${getScoreColor(stats.average_overall)}`}>
                        {stats.average_overall}%
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-teal-500/10 flex items-center justify-center">
                      <Target className="h-5 w-5 text-teal-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-t-amber-500">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Best Score</p>
                      <p className={`text-3xl font-bold mt-1 ${getScoreColor(stats.best_score)}`}>
                        {stats.best_score}%
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                      <Award className="h-5 w-5 text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-t-purple-500">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Practice</p>
                      <p className="text-3xl font-bold mt-1">{stats.total_attempts}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                      <BarChart3 className="h-5 w-5 text-purple-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-t-blue-500">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Improvement</p>
                      <p className={`text-3xl font-bold mt-1 ${stats.improvement > 0 ? 'text-emerald-500' : stats.improvement < 0 ? 'text-red-400' : ''}`}>
                        {stats.improvement > 0 ? '+' : ''}{stats.improvement}%
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      {improvementIcon}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Score Breakdown */}
            <div className="grid lg:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Accuracy</p>
                  <p className={`text-2xl font-bold ${getScoreColor(stats.average_accuracy)}`}>{stats.average_accuracy}%</p>
                  <Progress value={stats.average_accuracy} className="h-2 mt-2" />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Fluency</p>
                  <p className={`text-2xl font-bold ${getScoreColor(stats.average_fluency)}`}>{stats.average_fluency}%</p>
                  <Progress value={stats.average_fluency} className="h-2 mt-2" />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Completeness</p>
                  <p className={`text-2xl font-bold ${getScoreColor(stats.average_completeness)}`}>{stats.average_completeness}%</p>
                  <Progress value={stats.average_completeness} className="h-2 mt-2" />
                </CardContent>
              </Card>
            </div>

            {/* Trend Chart */}
            {trend.length > 1 && (
              <Card data-testid="pronunciation-trend-chart">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <TrendingUp className="h-5 w-5 text-teal-500" />
                    Score Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trend}>
                        <defs>
                          <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(v) => v.slice(5)}
                          stroke="#94a3b8"
                          fontSize={12}
                        />
                        <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={12} />
                        <Tooltip
                          contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                          labelStyle={{ color: '#94a3b8' }}
                          formatter={(value, name) => [`${value}%`, name === 'avg_score' ? 'Avg Score' : name]}
                        />
                        <Area type="monotone" dataKey="avg_score" stroke="#14b8a6" strokeWidth={2} fill="url(#scoreGradient)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* History Table */}
            <Card data-testid="pronunciation-history-table">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="h-5 w-5 text-purple-500" />
                  Practice History
                  <Badge variant="secondary" className="ml-2">{stats.total_attempts} attempts</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 text-muted-foreground font-medium">Text</th>
                        <th className="text-center py-3 px-2 text-muted-foreground font-medium">Overall</th>
                        <th className="text-center py-3 px-2 text-muted-foreground font-medium hidden sm:table-cell">Accuracy</th>
                        <th className="text-center py-3 px-2 text-muted-foreground font-medium hidden sm:table-cell">Fluency</th>
                        <th className="text-right py-3 px-2 text-muted-foreground font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((item) => (
                        <tr key={`${item.expected_text}-${item.timestamp || item.created_at}`} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="py-3 px-2">
                            <div className="font-medium truncate max-w-[200px]">{item.expected_text}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                              Said: {item.transcribed_text || '—'}
                            </div>
                          </td>
                          <td className="text-center py-3 px-2">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold border ${getScoreBg(item.overall_score)} ${getScoreColor(item.overall_score)}`}>
                              {item.overall_score}%
                            </span>
                          </td>
                          <td className="text-center py-3 px-2 hidden sm:table-cell">
                            <span className={getScoreColor(item.accuracy_score)}>{item.accuracy_score}%</span>
                          </td>
                          <td className="text-center py-3 px-2 hidden sm:table-cell">
                            <span className={getScoreColor(item.fluency_score)}>{item.fluency_score}%</span>
                          </td>
                          <td className="text-right py-3 px-2 text-muted-foreground text-xs">
                            {item.timestamp ? new Date(item.timestamp).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
